import { AfterViewInit, Component, Inject, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { TranslateService } from '@ngx-translate/core';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { ModuleImage } from 'app/library/module/models/module-image.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { IMaxBrainEnvironment } from 'app/projects/shared/src/lib/interfaces/environment.interface';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { AiCreatedImageApiService } from 'app/services/image-library/ai-created-image-api.service';
import { MyImageApiService } from 'app/services/image-library/my-image-api.service';
import { TenantLibraryApiService } from 'app/services/image-library/tenant-image-api.service';
import { UnsplashImageApiService } from 'app/services/image-library/unsplash-image-api.service';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { AiImage } from '../../models/ai-image.model';
import { MaxbrainImage } from '../../models/maxbrain-image.model';
import { MyImage } from '../../models/my-image.model';
import { TenantImage } from '../../models/tenant-image.model';
import { UnsplashImage } from '../../models/unsplash-image.model';
import { ImageLibraryApiService } from '../../services/image-library.api-service';
import { ImageUploadProgressComponent } from '../image-upload-progress/image-upload-progress.component';
import { ImageCropperDialogComponent } from '../image-cropper/image-cropper.component';

enum Tabs {
    MyImages = 0,
    TenantLibrary = 1,
    Unsplash = 2,
    AiImages = 3,
}

enum ImageUploadStatus {
    Uploading = 'uploading',
    Candeled = 'canceled',
    Failed = 'failed',
    Completed = 'completed',
}

enum MyImagesCategoriesName {
    RecentlyUsed = 'recently_used',
    MyImages = 'my_images',
    TenantLIbrary = 'tenant_library',
    Unsplash = 'unsplash',
    AIImages = 'open_ai',
    All = 'all',
}

enum UnsplashCategoriesName {
    New = 'new',
    Popular = 'popular',
    Random = 'random',
    All = 'all',
}

@Component({
    selector: 'app-dialog-select-mx-image',
    templateUrl: './select-mx-image.component.html',
    styleUrls: ['./select-mx-image.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class SelectMxImageLibraryDialogComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('tabGroup') tabGroup: MatTabGroup;

    @Input()
    previewImageWidth: number;
    @Input()
    previewImageHeight: number;
    @Input()
    freeCrop: boolean = false;

    searchInput = new FormControl();
    aiImagesSearchInput = new FormControl();

    imageDialogRef: MatDialogRef<ImageUploadProgressComponent, any>;

    imageCropperDialogRef: MatDialogRef<ImageCropperDialogComponent>;

    selectedTabIndex = 0;
    isDragOver = false;

    /**
     *  Method to use the Image Lib Generic
     */
    addMethod: (image: ModuleImage, tab?: Tabs, category?: string) => Observable<MaxbrainImage>;
    deleteMethod: (imageId: string) => Observable<any>;

    masonryTenantImages: TenantImage[] = [];
    masonryUnsplashImages: UnsplashImage[] = [];
    masonryMyImages: MyImage[] = [];
    masonryAiCreatedImages: any[] = [];
    myImageColumns: MyImage[][] = [[], [], [], []];
    tenantImageColumns: TenantImage[][] = [[], [], [], []];
    unsplashColumns: UnsplashImage[][] = [[], [], [], []];
    masonryAspectRatios: Record<string, string> = {};

    selectedImage: MyImage | TenantImage | UnsplashImage | AiImage = undefined;
    selectedCategory: MyImagesCategoriesName | UnsplashCategoriesName = MyImagesCategoriesName.RecentlyUsed;
    private lastUnsplashCategory: UnsplashCategoriesName = UnsplashCategoriesName.Popular;
    MyImagesCategoriesName = MyImagesCategoriesName;

    aiCreatedImage: AiImage;
    isAiImageLoading = false;
    isLoadingMoreImages = false;
    private imageListObserver: IntersectionObserver | null = null;
    private boundScrollContainerId: string | null = null;
    private suppressInfiniteScroll = false;
    private sentinelLeftView = false;

    myImagesCategories = [
        { name: MyImagesCategoriesName.RecentlyUsed, translation: 'IMAGE_LIBRARY.TABS.TENANT_LIBRARY.CATEGORIES.RECENTLY_USED' },
        { name: MyImagesCategoriesName.MyImages, translation: 'IMAGE_LIBRARY.TABS.TENANT_LIBRARY.CATEGORIES.UPLOADED_BY_ME' },
        { name: MyImagesCategoriesName.TenantLIbrary, translation: 'IMAGE_LIBRARY.TABS.TENANT_LIBRARY.CATEGORIES.SELECTED_FROM_TENANT_LIBRARY' },
        { name: MyImagesCategoriesName.Unsplash, translation: 'IMAGE_LIBRARY.TABS.TENANT_LIBRARY.CATEGORIES.SELECTED_FROM_UNSPLASH' },
        { name: MyImagesCategoriesName.AIImages, translation: 'IMAGE_LIBRARY.TABS.TENANT_LIBRARY.CATEGORIES.SELECTED_FROM_AI' },
        { name: MyImagesCategoriesName.All, translation: 'IMAGE_LIBRARY.TABS.TENANT_LIBRARY.CATEGORIES.ALL' },
    ];

    unsplashCategories = [
        { name: UnsplashCategoriesName.Popular, translation: 'IMAGE_LIBRARY.TABS.UNSPLASH.CATEGORIES.POPULAR' },
        { name: UnsplashCategoriesName.New, translation: 'IMAGE_LIBRARY.TABS.UNSPLASH.CATEGORIES.NEW' },
        { name: UnsplashCategoriesName.Random, translation: 'IMAGE_LIBRARY.TABS.UNSPLASH.CATEGORIES.RANDOM' },
        { name: UnsplashCategoriesName.All, translation: 'IMAGE_LIBRARY.TABS.UNSPLASH.CATEGORIES.ALL' },
    ];

    selectedFiles: FileList;
    fileList: File[] | any;
    uploadStatus: string;
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    unsplashViewsBaseUrl: string;
    isAiImagesFeatureActive = false;

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IMaxBrainEnvironment,
        private _translateService: TranslateService,
        private _matDialog: MatDialog,
        public dialogRef: MatDialogRef<SelectMxImageLibraryDialogComponent>,
        public imageLibraryApiService: ImageLibraryApiService,
        public myImageApiService: MyImageApiService,
        public tenantLibraryApiService: TenantLibraryApiService,
        public unsplashApiService: UnsplashImageApiService,
        public tenantDataService: TenantDataService,
        private _aiCreatedImageApiService: AiCreatedImageApiService,
        private _featureSwitchPipe: FeatureSwitchPipe
    ) {
        super();
        this.unsplashViewsBaseUrl = this._environment.unsplashViewsBaseUrl;
        this.isAiImagesFeatureActive = this._featureSwitchPipe.transform(FeatureSwitchName.AIImages);
    }

    async ngOnInit(): Promise<void> {
        if (!this.tenantDataService.tenantConfig.central_image_library.my_images) {
            this.myImagesCategories = this.myImagesCategories.filter((category) => category.name !== MyImagesCategoriesName.MyImages);
        }

        this.imageLibraryApiService.fetchingConfig = false;

        this.myImageApiService.fetchingMyImages = true;
        this.myImageApiService
            .getMyImagesFiltered(1, MyImagesCategoriesName.RecentlyUsed)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.myImageApiService.fetchingMyImages = false;
                setTimeout(() => {
                    this.bindImageListInfiniteScroll('myImagesContent');
                });
            });

        this.myImageApiService.myImages$.pipe(takeUntil(this._unsubscribeAll)).subscribe((myImages: MyImage[]) => {
            if (this.myImageApiService.myImagesPage > 1) {
                this.masonryMyImages = this.masonryMyImages.concat(myImages);
            } else {
                this.masonryMyImages = myImages;
                this.onFreshImageList('myImagesContent');
            }
            this.myImageColumns = this.splitIntoColumns(this.masonryMyImages);
            this.isLoadingMoreImages = false;
            setTimeout(() => {
                if (this.myImageApiService.myImagesPage <= 1) {
                    this.resetImageListScroll('myImagesContent');
                }
                this.bindImageListInfiniteScroll('myImagesContent');
                this.suppressInfiniteScroll = false;
            });
        });

        if (this.tenantDataService.tenantConfig.central_image_library.tenant_library) {
            this.tenantLibraryApiService.fetchingTenantImages = true;
            this.tenantLibraryApiService
                .getTenantImages(1, MyImagesCategoriesName.All)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this.tenantLibraryApiService.fetchingTenantImages = false;
                });

            this.tenantLibraryApiService.tenantImages$.pipe(takeUntil(this._unsubscribeAll)).subscribe((tenantImages: TenantImage[]) => {
                if (this.tenantLibraryApiService.tenantImagesPage > 1) {
                    this.masonryTenantImages = this.masonryTenantImages.concat(tenantImages);
                } else {
                    this.masonryTenantImages = tenantImages;
                    this.onFreshImageList('tenantImagesContent');
                }
                this.tenantImageColumns = this.splitIntoColumns(this.masonryTenantImages);
                this.isLoadingMoreImages = false;
                setTimeout(() => {
                    if (this.tenantLibraryApiService.tenantImagesPage <= 1) {
                        this.resetImageListScroll('tenantImagesContent');
                    }
                    this.bindImageListInfiniteScroll('tenantImagesContent');
                    this.suppressInfiniteScroll = false;
                });
            });
        }

        if (this.tenantDataService.tenantConfig.central_image_library.unsplash) {
            this.unsplashApiService.fetchingUnsplashImages = true;
            this.unsplashApiService
                .getUnsplashImagesFiltered(1, UnsplashCategoriesName.Popular)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this.unsplashApiService.fetchingUnsplashImages = false;
                });

            this.unsplashApiService.unsplashImages$.pipe(takeUntil(this._unsubscribeAll)).subscribe((unsplashImages: UnsplashImage[]) => {
                if (this.unsplashApiService.unsplashImagesPage > 1) {
                    this.masonryUnsplashImages = this.masonryUnsplashImages.concat(unsplashImages);
                } else {
                    this.masonryUnsplashImages = unsplashImages;
                    this.onFreshImageList('unsplashImagesContent');
                }
                this.unsplashColumns = this.splitIntoColumns(this.masonryUnsplashImages);
                this.isLoadingMoreImages = false;
                setTimeout(() => {
                    if (this.unsplashApiService.unsplashImagesPage <= 1) {
                        this.resetImageListScroll('unsplashImagesContent');
                    }
                    this.bindImageListInfiniteScroll('unsplashImagesContent');
                    this.suppressInfiniteScroll = false;
                });
            });
        }

        this.searchInput.valueChanges.pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe((searchTerm) => {
            if (this.selectedTabIndex !== Tabs.Unsplash) {
                return;
            }
            this.onFreshImageList('unsplashImagesContent');
            this.fetchUnsplashImages(1, searchTerm);
        });
    }

    ngAfterViewInit(): void {
        this.selectedTabIndex = this.tabGroup.selectedIndex;
    }

    openImageCropperDialog(image: MyImage | TenantImage | UnsplashImage | AiImage): void {
        this.imageCropperDialogRef = this._matDialog.open(ImageCropperDialogComponent, {
            width: '768px',
            minHeight: '80vh',
        });

        const instance = this.imageCropperDialogRef.componentInstance;

        instance.selectedImage = image;
        instance.previewImageWidth = this.previewImageWidth;
        instance.previewImageHeight = this.previewImageHeight;
        instance.freeCrop = this.freeCrop;

        instance.onCropped = async (file: File) => {
            this.uploadCroppedImage(file);
        };
    }

    private getImageListContainerId(): string | null {
        if (this.selectedTabIndex === Tabs.MyImages) {
            return 'myImagesContent';
        }

        if (this.selectedTabIndex === Tabs.TenantLibrary) {
            return 'tenantImagesContent';
        }

        if (this.selectedTabIndex === Tabs.Unsplash) {
            return 'unsplashImagesContent';
        }

        return null;
    }

    private resetImageListScroll(containerId?: string): void {
        const apply = (): void => {
            const listId = containerId || this.getImageListContainerId();
            const list = listId ? document.getElementById(listId) : null;

            if (list) {
                list.scrollTop = 0;
            }

            const dialog = document.querySelector('app-dialog-select-mx-image .image-library-dialog-container') as HTMLElement | null;
            if (dialog) {
                dialog.scrollTop = 0;
            }

            document.querySelectorAll('app-dialog-select-mx-image .mat-mdc-tab-body-content').forEach((element) => {
                (element as HTMLElement).scrollTop = 0;
            });
        };

        apply();
        requestAnimationFrame(() => {
            apply();
            requestAnimationFrame(apply);
        });
    }

    private onFreshImageList(containerId: string): void {
        this.suppressInfiniteScroll = true;
        this.sentinelLeftView = false;
        this.boundScrollContainerId = null;
        this.imageListObserver?.disconnect();
        this.imageListObserver = null;
        this.resetImageListScroll(containerId);
    }

    private bindImageListInfiniteScroll(containerId: string): void {
        const root = document.getElementById(containerId);
        const sentinel = root?.querySelector('.image-scroll-sentinel') as HTMLElement | null;

        if (!root || !sentinel) {
            this.boundScrollContainerId = null;
            return;
        }

        if (this.boundScrollContainerId === containerId && this.imageListObserver) {
            return;
        }

        this.imageListObserver?.disconnect();
        this.boundScrollContainerId = containerId;
        this.sentinelLeftView = false;
        this.imageListObserver = new IntersectionObserver(
            (entries) => {
                const isIntersecting = entries.some((entry) => entry.isIntersecting);

                if (!isIntersecting) {
                    this.sentinelLeftView = true;
                    return;
                }

                if (!this.sentinelLeftView || this.boundScrollContainerId !== this.getImageListContainerId()) {
                    return;
                }

                this.loadMoreImages();
            },
            { root, rootMargin: '80px', threshold: 0 }
        );
        this.imageListObserver.observe(sentinel);
    }

    trackByImageId(_index: number, image: { id: string }): string {
        return image.id;
    }

    onMasonryImageLoad(event: Event): void {
        (event.target as HTMLElement).classList.add('is-loaded');
    }

    private splitIntoColumns<T extends { id?: string; width?: number; height?: number }>(items: T[]): T[][] {
        const columnCount = 4;
        const columns: T[][] = Array.from({ length: columnCount }, () => []);
        const heights = new Array(columnCount).fill(0);

        items.forEach((item) => {
            if (item.id) {
                this.masonryAspectRatios[item.id] = this.getClampedAspectRatio(item);
            }

            let shortest = 0;
            for (let i = 1; i < columnCount; i++) {
                if (heights[i] < heights[shortest]) {
                    shortest = i;
                }
            }

            columns[shortest].push(item);
            heights[shortest] += this.getRelativeTileHeight(item);
        });

        return columns;
    }

    private getClampedAspectRatio(item: { width?: number; height?: number }): string {
        return `${this.getClampedWidthPerHeight(item)} / 1`;
    }

    private getRelativeTileHeight(item: { width?: number; height?: number }): number {
        return 1 / this.getClampedWidthPerHeight(item);
    }

    private getClampedWidthPerHeight(item: { width?: number; height?: number }): number {
        const width = item.width || 4;
        const height = item.height || 3;
        const minRatio = 0.65;
        const maxRatio = 1.7;
        const ratio = width / height;

        if (ratio > maxRatio) {
            return maxRatio;
        }

        if (ratio < minRatio) {
            return minRatio;
        }

        return ratio;
    }

    onImageListScroll(event: Event): void {
        const container = event.target as HTMLElement;

        if (container.scrollTop < 24) {
            return;
        }

        if (container.scrollTop + container.clientHeight < container.scrollHeight - 160) {
            return;
        }

        this.loadMoreImages();
    }

    private loadMoreImages(): void {
        if (this.isLoadingMoreImages || this.suppressInfiniteScroll) {
            return;
        }

        const container = document.getElementById(this.getImageListContainerId());
        if (container && container.scrollTop < 24 && container.scrollHeight > container.clientHeight + 160) {
            return;
        }

        if (this.selectedTabIndex === Tabs.MyImages) {
            if (
                !this.tenantDataService.tenantConfig.central_image_library.my_images ||
                this.myImageApiService.myImagesPage >= this.myImageApiService.tenantImagesPagination.numberOfPages
            ) {
                return;
            }

            this.isLoadingMoreImages = true;

            if (this.selectedCategory === MyImagesCategoriesName.All) {
                this.myImageApiService
                    .getMyImages(++this.myImageApiService.myImagesPage)
                    .pipe(
                        takeUntil(this._unsubscribeAll),
                        finalize(() => (this.isLoadingMoreImages = false))
                    )
                    .subscribe();
            } else {
                this.myImageApiService
                    .getMyImagesFiltered(++this.myImageApiService.myImagesPage, this.selectedCategory)
                    .pipe(
                        takeUntil(this._unsubscribeAll),
                        finalize(() => (this.isLoadingMoreImages = false))
                    )
                    .subscribe();
            }

            return;
        }

        if (this.selectedTabIndex === Tabs.TenantLibrary) {
            if (
                !this.tenantDataService.tenantConfig.central_image_library.tenant_library ||
                this.tenantLibraryApiService.tenantImagesPage >= this.tenantLibraryApiService.tenantImagesPagination.numberOfPages
            ) {
                return;
            }

            this.isLoadingMoreImages = true;
            this.tenantLibraryApiService
                .getTenantImages(++this.tenantLibraryApiService.tenantImagesPage, this.selectedCategory)
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    finalize(() => (this.isLoadingMoreImages = false))
                )
                .subscribe();

            return;
        }

        if (this.selectedTabIndex === Tabs.Unsplash) {
            if (
                !this.tenantDataService.tenantConfig.central_image_library.unsplash ||
                (this.unsplashApiService.unsplashImagesPagination.numberOfPages > 0 &&
                    this.unsplashApiService.unsplashImagesPage >= this.unsplashApiService.unsplashImagesPagination.numberOfPages)
            ) {
                return;
            }

            this.fetchUnsplashImages(this.unsplashApiService.unsplashImagesPage + 1, this.searchInput.value);
        }
    }

    private isUnsplashCategory(category: string): category is UnsplashCategoriesName {
        return Object.values(UnsplashCategoriesName).includes(category as UnsplashCategoriesName);
    }

    private fetchUnsplashImages(page = 1, searchTerm: string = this.searchInput.value): void {
        const term = searchTerm?.trim() ? searchTerm.trim() : undefined;
        this.isLoadingMoreImages = true;

        const request$ =
            this.selectedCategory === UnsplashCategoriesName.All
                ? this.unsplashApiService.getUnsplashImages(page, term)
                : this.unsplashApiService.getUnsplashImagesFiltered(page, this.selectedCategory, term);

        request$
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => (this.isLoadingMoreImages = false))
            )
            .subscribe();
    }

    onSelectImage(image: MyImage | TenantImage | UnsplashImage | AiImage): void {
        const isSameImage = this.selectedImage === image;

        if (this.selectedImage) {
            this.selectedImage.selected = false;
        }

        if (isSameImage) {
            this.selectedImage = undefined;
            return;
        }

        image.selected = true;
        this.selectedImage = image;

        this.openImageCropperDialog(image);
    }

    generateImage(searchTerm: string): void {
        this.isAiImageLoading = true;

        this._aiCreatedImageApiService
            .generateImage(searchTerm)
            .then((result) => {
                this.aiCreatedImage = result;
                this.onSelectImage(this.aiCreatedImage);
            })
            .finally(() => {
                this.isAiImageLoading = false;
            });
    }

    onGetUnsplashImages(category: UnsplashCategoriesName): void {
        this.selectedCategory = category;
        this.lastUnsplashCategory = category;
        this.onFreshImageList('unsplashImagesContent');
        this.fetchUnsplashImages(1, this.searchInput.value);
    }

    onGetMyImages(category: MyImagesCategoriesName): void {
        this.selectedCategory = category;
        this.onFreshImageList('myImagesContent');
        if (this.selectedCategory === MyImagesCategoriesName.All) {
            this.myImageApiService.getMyImages(1).subscribe(() => {
                this.myImageApiService.fetchingMyImages = false;
                this.selectedImage = undefined;
            });
        } else {
            this.myImageApiService
                .getMyImagesFiltered(1, this.selectedCategory)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this.selectedImage = undefined;
                });
        }
    }

    onGetTenantImages(category: MyImagesCategoriesName): void {
        this.selectedCategory = category;
        this.onFreshImageList('tenantImagesContent');
        this.tenantLibraryApiService
            .getTenantImages(1, this.selectedCategory)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.selectedImage = undefined;
            });
    }

    onTabChanged(event: MatTabChangeEvent): void {
        this.selectedImage = undefined;
        // If tenant library is disabled, we're missing tenant library tab
        // if it doesn't exist, increase it by one to target unsplash tab by enum
        if (!this.tenantDataService.tenantConfig.central_image_library.tenant_library && event.index >= Tabs.TenantLibrary) {
            event.index++;
        }

        // Uncomment  if UNSPLASH is not a last tab
        /* if (!this._tenantDataService.tenantConfig.central_image_library.unsplash && event.index >= TABS.UNSPLASH) {
            event.index++
        } */

        if (this.selectedTabIndex === Tabs.Unsplash && this.isUnsplashCategory(this.selectedCategory)) {
            this.lastUnsplashCategory = this.selectedCategory;
        }

        // we have to know on which tab we are to correctly call pagination and get selected category
        this.selectedTabIndex = event.index;
        this.suppressInfiniteScroll = true;
        this.boundScrollContainerId = null;
        this.imageListObserver?.disconnect();
        this.imageListObserver = null;
        this.resetImageListScroll();

        const bindCurrentTab = (containerId: string): void => {
            setTimeout(() => {
                this.resetImageListScroll(containerId);
                this.bindImageListInfiniteScroll(containerId);
                this.suppressInfiniteScroll = false;
            });
        };

        if (this.selectedTabIndex === Tabs.MyImages) {
            this.selectedCategory = MyImagesCategoriesName.RecentlyUsed;
            bindCurrentTab('myImagesContent');
        } else if (this.selectedTabIndex === Tabs.TenantLibrary) {
            this.selectedCategory = MyImagesCategoriesName.All;
            bindCurrentTab('tenantImagesContent');
        } else if (this.selectedTabIndex === Tabs.Unsplash) {
            this.selectedCategory = this.lastUnsplashCategory || UnsplashCategoriesName.Popular;
            this.fetchUnsplashImages(1, this.searchInput.value);
            bindCurrentTab('unsplashImagesContent');
        } else {
            this.suppressInfiniteScroll = false;
        }
    }

    async uploadNewImages(files: File[]): Promise<void> {
        if (files && files[0]) {
            this.fileList = files;
            this.openImageUploadDialog();
            this.imageDialogRef.componentInstance.uploadStatus = ImageUploadStatus.Uploading;
            let isUploaded = false;
            for (const file of this.fileList) {
                isUploaded = await this.myImageApiService.uploadNewImage(file);
                if (!isUploaded) {
                    this.imageDialogRef.componentInstance.uploadStatus = ImageUploadStatus.Failed;
                    if (this.myImageApiService.cancelUploadingImages) {
                        this.imageDialogRef.componentInstance.uploadStatus = ImageUploadStatus.Candeled;
                    }
                    break;
                } else {
                    if (this.imageDialogRef) {
                        this.imageDialogRef.componentInstance.successCounter++;
                    }
                    file.uploaded = true;
                }
            }
            if (isUploaded && !this.myImageApiService.cancelUploadingImages) {
                this.imageDialogRef.componentInstance.uploadStatus = ImageUploadStatus.Completed;
            }

            this.imageDialogRef.componentInstance.fileList = this.fileList;
            this.selectedCategory = MyImagesCategoriesName.MyImages;
            this.onGetMyImages(this.selectedCategory);
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    onFileDropped(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;

        const files = event.dataTransfer?.files;
        if (files?.length) {
            this.uploadNewImages(Array.from(files));
        }
    }

    /**
     * Attaches the uploaded image via addMethod (if provided), then closes the dialog.
     * Must wait for addMethod to finish — closing earlier destroys this component and
     * aborts the in-flight PUT via takeUntil(_unsubscribeAll).
     */
    private finishImageSelection(maxBrainImage: MaxbrainImage, category?: string): void {
        if (!this.addMethod) {
            this.myImageApiService.setUploadingCroppedImage(false);
            this.selectedImage = undefined;
            this.dialogRef.close(maxBrainImage);
            return;
        }

        this.addMethod(maxBrainImage, this.selectedTabIndex, category)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this.myImageApiService.setUploadingCroppedImage(false);
                    this.selectedImage = undefined;
                    this.dialogRef.close(maxBrainImage);
                },
                error: () => {
                    this.myImageApiService.setUploadingCroppedImage(false);
                },
            });
    }

    uploadMyImages(croppedImage: File): void {
        switch (this.selectedCategory) {
            case MyImagesCategoriesName.RecentlyUsed:
            case MyImagesCategoriesName.AIImages:
            case MyImagesCategoriesName.All:
            case MyImagesCategoriesName.MyImages:
                if (this.selectedImage instanceof MyImage) {
                    this.myImageApiService
                        .uploadCroppedImage(croppedImage, this.selectedImage)
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe((maxBrainImage: MaxbrainImage) => {
                            this.finishImageSelection(maxBrainImage, this.selectedCategory);
                        });
                }
                break;
            case MyImagesCategoriesName.TenantLIbrary:
                this.tenantLibraryApiService
                    .uploadImage(croppedImage, (this.selectedImage as TenantImage).id)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((maxBrainImage: MaxbrainImage) => {
                        this.finishImageSelection(maxBrainImage);
                    });
                break;
            case MyImagesCategoriesName.Unsplash:
                const image = this.selectedImage as MyImage;
                this.unsplashApiService
                    .uploadImage(croppedImage, image.unsplash.unsplashId)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((maxBrainImage: MaxbrainImage) => {
                        this.finishImageSelection(maxBrainImage);
                    });
                break;
        }
    }

    uploadTenantLibrary(croppedImage: File): void {
        this.tenantLibraryApiService
            .uploadImage(croppedImage, (this.selectedImage as TenantImage).id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((maxBrainImage: MaxbrainImage) => {
                this.finishImageSelection(maxBrainImage);
            });
    }

    uploadUnsplash(croppedImage: File): void {
        this.unsplashApiService
            .uploadImage(croppedImage, (this.selectedImage as UnsplashImage).id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((maxBrainImage: MaxbrainImage) => {
                this.finishImageSelection(maxBrainImage);
            });
    }

    async uploadAiImage(croppedImage: File): Promise<void> {
        const base64Response = await fetch(`data:image/png;base64,${(this.selectedImage as AiImage).image}`);
        const blob = await base64Response.blob();
        const selectedImageFile = new File([blob], 'originalImage', { type: blob.type });

        this._aiCreatedImageApiService
            .uploadImage(croppedImage, selectedImageFile)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((maxBrainImage: MaxbrainImage) => {
                this.finishImageSelection(maxBrainImage);
            });
    }

    uploadCroppedImage(croppedImage: File): void {
        this.myImageApiService.setUploadingCroppedImage(true);
        switch (this.selectedTabIndex) {
            case Tabs.MyImages:
                this.uploadMyImages(croppedImage);
                break;
            case Tabs.TenantLibrary:
                this.uploadTenantLibrary(croppedImage);
                break;
            case Tabs.Unsplash:
                this.uploadUnsplash(croppedImage);
                break;
            case Tabs.AiImages:
                this.uploadAiImage(croppedImage);
                break;
        }
    }

    openDeleteImageDialog(imageId: string): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('MODULE.MESSAGE.DELETE_IMAGE_CONFIRMATION');
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.DELETE';

        this.confirmDialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                if (result) {
                    this.myImageApiService
                        .deleteModuleImage(imageId)
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(() => {
                            this.masonryMyImages = this.masonryMyImages.filter((image) => image.id !== imageId);
                            this.myImageColumns = this.splitIntoColumns(this.masonryMyImages);
                        });
                }

                this.confirmDialogRef = null;
            });
    }

    openImageUploadDialog(): void {
        this.imageDialogRef = this._matDialog.open(ImageUploadProgressComponent, {
            width: '600px',
            disableClose: true,
        });

        this.imageDialogRef.componentInstance.fileList = this.fileList;
    }

    handleKeyUp(e: KeyboardEvent, searchTerm: string) {
        if (e.key === 'Enter') {
            this.generateImage(searchTerm);
        }
    }

    ngOnDestroy(): void {
        this.imageListObserver?.disconnect();
        this.boundScrollContainerId = null;
        this.myImageApiService.setUploadingCroppedImage(false);
        super.ngOnDestroy();
    }
}
